import React, { useState } from 'react';
import { Box, Typography, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Paper, Avatar, Chip, IconButton, Dialog, DialogContent, DialogTitle, Tooltip, Divider } from '@mui/material';
import { Visibility, ArrowBack, Close as CloseIcon } from '@mui/icons-material';
import CreateAppraisal from './createAppraisal';
import { useNavigate } from 'react-router-dom';

const MOCK_APPRAISALS = [
    { id: 1, name: 'Alex Johnson', role: 'Frontend Developer', period: 'Q1 2024', status: 'Submitted for Review', score: 0, avatar: 'A' },
    { id: 2, name: 'Sarah Williams', role: 'UI Designer', period: 'Q1 2024', status: 'Completed', score: 4.8, avatar: 'S' },
    { id: 3, name: 'Michael Chen', role: 'Backend Lead', period: 'Q1 2024', status: 'Pending', score: 0, avatar: 'M' },
    { id: 4, name: 'Emily Lee', role: 'Project Manager', period: 'Q1 2024', status: 'In Progress', score: 0, avatar: 'E' },
    { id: 5, name: 'David Kim', role: 'QA Engineer', period: 'Q1 2024', status: 'Overdue', score: 3, avatar: 'D' },
];

function PastAppraisals() {
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);

    // Fix 1: Initialize the pagination state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const getStatusColor = (status) => {
        switch (status) {
            case 'Completed': return { bg: '#E1FCEF', text: '#14532D' };
            case 'Pending Manager': return { bg: '#FFF7ED', text: '#9A3412' };
            case 'Pending Employee': return { bg: '#EFF6FF', text: '#1E40AF' };
            default: return { bg: '#F3F4F6', text: '#374151' };
        }
    };

    // Fix 2: Handle page change
    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    // Fix 3: Handle rows per page change
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Fix 4: Slice the data based on current page and rows per page
    const paginatedData = MOCK_APPRAISALS.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    return (
        <Box sx={{ p: 1 }}>
            <Stack direction="row" alignItems="center" sx={{ borderBottom: '1px solid #c3c3c3', pb: 1 }}>
                <IconButton
                    onClick={() => navigate('/manager/performance-management', { state: { activeTab: 1 } })}
                    sx={{ color: '#64748b', borderRadius: '8px' }}
                >
                    <ArrowBack sx={{ fontSize: 20 }} />
                    <Typography variant="body2" sx={{ ml: 1, fontWeight: 500 }}>Back</Typography>
                </IconButton>
                <Divider orientation="vertical" flexItem sx={{ mx: 3, height: 32 }} />
                <Box>
                    <Typography variant="h6" fontWeight="bold">Appraisal History</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Viewing all past appraisals for <b>Bharath M</b>
                    </Typography>
                </Box>
            </Stack>

            <TableContainer component={Paper} sx={{ borderRadius: '12px', boxShadow: '0 10px 30px rgba(0,0,0,0.04)', overflow: 'hidden', border: '1px solid #E2E8F0', bgcolor: '#FFFFFF', mt: 2 }}>
                <Box sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 900 }}>
                        <TableHead sx={{ bgcolor: '#EFF6FF' }}>
                            <TableRow>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Employee</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Period</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Status</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }}>Score</TableCell>
                                <TableCell sx={{ color: '#1D4ED8', fontWeight: 700 }} align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {/* Fix 5: Map over the sliced paginatedData instead of full array */}
                            {paginatedData.map((row) => (
                                <TableRow key={row.id} hover sx={{ '&:hover': { bgcolor: '#F1F5F9 !important' } }}>
                                    <TableCell>
                                        <Stack direction="row" spacing={2} alignItems="center">
                                            <Avatar sx={{ bgcolor: '#6366F1', fontSize: '14px' }}>{row.avatar}</Avatar>
                                            <Box>
                                                <Typography variant="body2" fontWeight="600">{row.name}</Typography>
                                                <Typography variant="caption" color="textSecondary">{row.role}</Typography>
                                            </Box>
                                        </Stack>
                                    </TableCell>
                                    <TableCell><Typography variant="body2">{row.period}</Typography></TableCell>
                                    <TableCell>
                                        <Chip
                                            label={row.status}
                                            size="small"
                                            sx={{
                                                bgcolor: getStatusColor(row.status).bg,
                                                color: getStatusColor(row.status).color,
                                                fontWeight: 700,
                                                fontSize: '0.7rem',
                                                border: `1px solid ${getStatusColor(row.status).border || '#cbd5e1'}`
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" fontWeight="700">
                                            {row.score > 0 ? row.score : '--'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Tooltip title="View Details">
                                            <IconButton size="small" onClick={() => navigate('/manager/view-appraisals')}>
                                                <Visibility fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            </TableContainer>

            {/* Fix 6: Correct props passed to TablePagination */}
            <TablePagination
                component="div"
                count={MOCK_APPRAISALS.length}
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25]}
            />

            <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
                <DialogTitle sx={{ borderBottom: '1px solid #eee', mb: 1 }}>
                    Create New Appraisal
                    <IconButton onClick={() => setOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent>
                    <CreateAppraisal onClose={() => setOpen(false)} />
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default PastAppraisals;